import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { useProducts } from '@/hooks/useProducts';
import { OutletStatusBadge } from '@/components/outlets/OutletStatusBadge';
import { OutletDeviceStatusGrid } from '@/components/outlets/OutletDeviceStatusGrid';
import { OutletForm } from '@/components/outlets/OutletForm';
import type {
  Outlet,
  OutletStatusInfo,
  UpdateOutletRequest,
  CreateOutletRequest,
  Product,
  OutletPricing,
} from '@photobooth/types';

export function OutletDetailPage() {
  const { outletId } = useParams<{ outletId: string }>();
  const navigate = useNavigate();
  const [outlet, setOutlet] = useState<Outlet | null>(null);
  const [status, setStatus] = useState<OutletStatusInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [error, setError] = useState('');
  const [customerPinValue, setCustomerPinValue] = useState('');
  const [capturePinValue, setCapturePinValue] = useState('');
  const [isUpdatingCustomerPin, setIsUpdatingCustomerPin] = useState(false);
  const [isUpdatingCapturePin, setIsUpdatingCapturePin] = useState(false);
  const [customerPinSuccess, setCustomerPinSuccess] = useState(false);
  const [capturePinSuccess, setCapturePinSuccess] = useState(false);
  const [outletPricingMap, setOutletPricingMap] = useState<Record<string, OutletPricing>>({});
  const [pricingForm, setPricingForm] = useState<Record<
    string,
    { originalPrice?: string; discountedPrice?: string; isPromotionActive?: boolean }
  >>({});
  const [savingProductId, setSavingProductId] = useState<string | null>(null);
  const [removingProductId, setRemovingProductId] = useState<string | null>(null);
  const { products } = useProducts();

  const fetchOutletPricing = useCallback(async () => {
    if (!outletId) return;
    const response = await api.products.getByOutlet(outletId);
    if (response.success && response.data) {
      const map: Record<string, OutletPricing> = {};
      for (const p of response.data) {
        if (p.pricing) map[p.productId] = p.pricing;
      }
      setOutletPricingMap(map);
    }
  }, [outletId]);

  useEffect(() => {
    if (outletId) {
      fetchOutlet();
      fetchStatus();
    }
  }, [outletId]);

  useEffect(() => {
    if (outletId && outlet) {
      fetchOutletPricing();
    }
  }, [outletId, outlet, fetchOutletPricing]);

  const fetchOutlet = async () => {
    if (!outletId) return;
    
    try {
      setIsLoading(true);
      const response = await api.outlets.getById(outletId);
      if (response.success && response.data) {
        setOutlet(response.data);
      }
    } catch (err) {
      setError('Failed to fetch outlet');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchStatus = async () => {
    if (!outletId) return;
    
    try {
      const response = await api.outlets.getStatus(outletId);
      if (response.success && response.data) {
        setStatus(response.data);
      }
    } catch (err) {
      console.error('Failed to fetch status:', err);
    }
  };

  const handleUpdate = async (data: CreateOutletRequest) => {
    if (!outletId) return;
    
    const updateData: UpdateOutletRequest = {
      name: data.name,
      location: data.location,
      address: data.address,
      mapsUrl: data.mapsUrl,
      isPublic: data.isPublic,
      productIds: data.productIds,
      configuration: data.configuration,
    };
    
    const response = await api.outlets.update(outletId, updateData);
    if (response.success && response.data) {
      setOutlet(response.data);
      setIsEditing(false);
    }
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !outletId) return;
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }
    setIsUploadingPhoto(true);
    try {
      const response = await api.outlets.uploadPhoto(outletId, file);
      if (response.success && response.data) {
        setOutlet((prev) => (prev ? { ...prev, imageUrl: response.data!.url } : null));
      } else {
        alert('Failed to upload photo');
      }
    } catch (err) {
      alert('Failed to upload photo');
    } finally {
      setIsUploadingPhoto(false);
      e.target.value = '';
    }
  };

  const handleDelete = async () => {
    if (!outletId || !confirm('Are you sure you want to delete this outlet?')) return;
    
    try {
      await api.outlets.delete(outletId);
      navigate('/outlets');
    } catch (err) {
      setError('Failed to delete outlet');
    }
  };

  const handleUpdateCustomerPin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!outletId || !customerPinValue) return;

    // Validate PIN format
    if (!/^\d{4,6}$/.test(customerPinValue)) {
      alert('PIN must be 4-6 digits');
      return;
    }

    setIsUpdatingCustomerPin(true);
    setCustomerPinSuccess(false);
    
    try {
      const response = await api.outlets.updateRolePin(outletId, 'customer', customerPinValue);
      if (response.success) {
        setCustomerPinSuccess(true);
        setCustomerPinValue('');
        setTimeout(() => setCustomerPinSuccess(false), 3000);
      } else {
        alert(response.error?.message || 'Failed to update Customer PIN');
      }
    } catch (err) {
      alert('Failed to update Customer PIN');
    } finally {
      setIsUpdatingCustomerPin(false);
    }
  };

  const handleUpdateCapturePin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!outletId || !capturePinValue) return;

    // Validate PIN format
    if (!/^\d{4,6}$/.test(capturePinValue)) {
      alert('PIN must be 4-6 digits');
      return;
    }

    setIsUpdatingCapturePin(true);
    setCapturePinSuccess(false);
    
    try {
      const response = await api.outlets.updateRolePin(outletId, 'capture', capturePinValue);
      if (response.success) {
        setCapturePinSuccess(true);
        setCapturePinValue('');
        setTimeout(() => setCapturePinSuccess(false), 3000);
      } else {
        alert(response.error?.message || 'Failed to update Capture PIN');
      }
    } catch (err) {
      alert('Failed to update Capture PIN');
    } finally {
      setIsUpdatingCapturePin(false);
    }
  };

  const handleSavePricing = async (product: Product) => {
    if (!outletId) return;
    const form = pricingForm[product.productId];
    const existing = outletPricingMap[product.productId];
    const originalPrice = form?.originalPrice
      ? parseFloat(form.originalPrice)
      : existing?.originalPrice ?? product.basePrice;
    if (isNaN(originalPrice) || originalPrice <= 0) {
      alert('Original price must be a positive number');
      return;
    }
    const discountedPrice = form?.discountedPrice
      ? parseFloat(form.discountedPrice)
      : undefined;
    if (discountedPrice !== undefined && (isNaN(discountedPrice) || discountedPrice > originalPrice)) {
      alert('Discounted price must be less than or equal to original price');
      return;
    }
    setSavingProductId(product.productId);
    try {
      const response = await api.products.setOutletPricing(product.productId, outletId, {
        originalPrice,
        discountedPrice: discountedPrice && discountedPrice > 0 ? discountedPrice : undefined,
        isPromotionActive: form?.isPromotionActive ?? existing?.isPromotionActive ?? false,
      });
      if (response.success && response.data) {
        setOutletPricingMap((prev) => ({ ...prev, [product.productId]: response.data! }));
      } else {
        alert(response.error?.message || 'Failed to save pricing');
      }
    } catch (err) {
      alert('Failed to save pricing');
    } finally {
      setSavingProductId(null);
    }
  };

  const handleRemovePricing = async (product: Product) => {
    if (!outletId || !confirm('Remove outlet-specific pricing for this product?')) return;
    setRemovingProductId(product.productId);
    try {
      const response = await api.products.deleteOutletPricing(product.productId, outletId);
      if (response.success) {
        setOutletPricingMap((prev) => {
          const next = { ...prev };
          delete next[product.productId];
          return next;
        });
        setPricingForm((prev) => {
          const next = { ...prev };
          delete next[product.productId];
          return next;
        });
      } else {
        alert(response.error?.message || 'Failed to remove pricing');
      }
    } catch (err) {
      alert('Failed to remove pricing');
    } finally {
      setRemovingProductId(null);
    }
  };

  function getPricingFormValue(product: Product, field: 'originalPrice'): string;
  function getPricingFormValue(product: Product, field: 'discountedPrice'): string;
  function getPricingFormValue(product: Product, field: 'isPromotionActive'): boolean;
  function getPricingFormValue(product: Product, field: 'originalPrice' | 'discountedPrice' | 'isPromotionActive'): string | boolean {
    const existing = outletPricingMap[product.productId];
    const form = pricingForm[product.productId];
    if (form && field in form) {
      const v = form[field];
      if (field === 'isPromotionActive') return v as boolean;
      return v as string;
    }
    if (field === 'originalPrice') return existing ? String(existing.originalPrice) : String(product.basePrice);
    if (field === 'discountedPrice') return existing?.discountedPrice ? String(existing.discountedPrice) : '';
    return existing?.isPromotionActive ?? false;
  }

  const setPricingFormValue = (
    productId: string,
    field: 'originalPrice' | 'discountedPrice' | 'isPromotionActive',
    value: string | boolean
  ) => {
    setPricingForm((prev) => ({
      ...prev,
      [productId]: {
        ...prev[productId],
        [field]: value,
      },
    }));
  };

  if (isLoading) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (error || !outlet) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error || 'Outlet not found'}
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mb-6">
        <button
          onClick={() => navigate('/outlets')}
          className="text-primary hover:underline mb-4"
        >
          ← Back to Outlets
        </button>
        
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-text">{outlet.name}</h1>
            <p className="text-text-muted mt-1">{outlet.location}</p>
          </div>
          {status && <OutletStatusBadge status={status.status} />}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Terminal Status */}
          {status && (
            <div className="bg-surface rounded-lg shadow p-6">
              <h2 className="text-xl font-bold text-text mb-4">Terminal Status</h2>
              <OutletDeviceStatusGrid deviceCounts={status.deviceCounts} />
            </div>
          )}

          {/* Outlet Details */}
          <div className="bg-surface rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-text">Outlet Details</h2>
              {!isEditing && (
                <button
                  onClick={() => setIsEditing(true)}
                  className="text-primary hover:underline"
                >
                  Edit
                </button>
              )}
            </div>

            {isEditing ? (
              <OutletForm
                outlet={outlet}
                products={products}
                onSubmit={handleUpdate}
                onCancel={() => setIsEditing(false)}
              />
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-text-muted">Name</label>
                  <p className="text-text">{outlet.name}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-text-muted">Location</label>
                  <p className="text-text">{outlet.location}</p>
                </div>
                {outlet.address && (
                  <div>
                    <label className="text-sm font-medium text-text-muted">Address</label>
                    <p className="text-text">{outlet.address}</p>
                  </div>
                )}
                <div>
                  <label className="text-sm font-medium text-text-muted">Google Maps</label>
                  {outlet.mapsUrl ? (
                    <a
                      href={outlet.mapsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline block truncate"
                    >
                      Open in Google Maps
                    </a>
                  ) : (
                    <p className="text-text-muted italic">Not set</p>
                  )}
                </div>
                <div>
                  <label className="text-sm font-medium text-text-muted">Sync Interval</label>
                  <p className="text-text">{outlet.configuration.syncInterval} minutes</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-text-muted">Status</label>
                  <p className="text-text">{outlet.status}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-text-muted">Available on website</label>
                  <p className="text-text">{outlet.isPublic ? 'Yes' : 'No'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-text-muted">Products available</label>
                  {(() => {
                    const productList =
                      outlet.productIds && outlet.productIds.length > 0
                        ? outlet.productIds
                            .map((id) => products.find((p) => p.productId === id))
                            .filter((p): p is NonNullable<typeof p> => !!p)
                        : [];
                    return productList.length > 0 ? (
                      <ul className="mt-1 space-y-1">
                        {productList.map((p) => (
                          <li key={p.productId} className="text-text">
                            {p.name}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-text-muted italic">No products assigned</p>
                    );
                  })()}
                </div>
              </div>
            )}
          </div>

          {/* Outlet Pricing */}
          <div className="bg-surface rounded-lg shadow p-6">
            <h2 className="text-xl font-bold text-text mb-4">Outlet Pricing</h2>
            <p className="text-sm text-text-muted mb-2">
              Set different prices for products at this outlet. Leave empty to use base price.
            </p>
            <div className="text-xs text-text-muted mb-4 bg-gray-50 rounded-md p-3 border border-gray-100">
              <p className="font-medium text-gray-600 mb-1">Pricing logic:</p>
              <ul className="list-disc list-inside space-y-0.5">
                <li><strong>Original</strong>: Price charged at this outlet (defaults to base price)</li>
                <li><strong>Discount</strong>: Optional promotional price (must be lower than original)</li>
                <li><strong>Promo</strong>: When checked, the discounted price is used. When unchecked, the original price is used.</li>
              </ul>
            </div>
            {(() => {
              const productList: Product[] =
                outlet.productIds && outlet.productIds.length > 0
                  ? outlet.productIds
                      .map((id) => products.find((p) => p.productId === id))
                      .filter((p): p is Product => !!p)
                  : [];
              if (productList.length === 0) {
                return (
                  <p className="text-text-muted italic">No products available. Add products to the outlet first.</p>
                );
              }
              return (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-text-muted uppercase">Product</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-text-muted uppercase">Base price</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-text-muted uppercase">Original</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-text-muted uppercase">Discount</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-text-muted uppercase">Promo</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-text-muted uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {productList.map((product) => (
                        <tr key={product.productId}>
                          <td className="px-4 py-3 text-sm text-text">{product.name}</td>
                          <td className="px-4 py-3 text-sm text-text-muted">
                            Rp {product.basePrice.toLocaleString()}
                          </td>
                          <td className="px-4 py-2">
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={getPricingFormValue(product, 'originalPrice')}
                              onChange={(e) =>
                                setPricingFormValue(product.productId, 'originalPrice', e.target.value)
                              }
                              className="w-24 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-primary"
                            />
                          </td>
                          <td className="px-4 py-2">
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              placeholder="Optional"
                              value={getPricingFormValue(product, 'discountedPrice')}
                              onChange={(e) =>
                                setPricingFormValue(product.productId, 'discountedPrice', e.target.value)
                              }
                              className="w-24 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-primary"
                            />
                          </td>
                          <td className="px-4 py-2">
                            <input
                              type="checkbox"
                              checked={getPricingFormValue(product, 'isPromotionActive')}
                              onChange={(e) =>
                                setPricingFormValue(product.productId, 'isPromotionActive', e.target.checked)
                              }
                              className="w-4 h-4 text-primary border-gray-300 rounded"
                            />
                          </td>
                          <td className="px-4 py-2 text-right space-x-2">
                            <button
                              onClick={() => handleSavePricing(product)}
                              disabled={savingProductId === product.productId}
                              className="text-primary hover:underline text-sm disabled:opacity-50"
                            >
                              {savingProductId === product.productId ? 'Saving...' : 'Save'}
                            </button>
                            {outletPricingMap[product.productId] && (
                              <button
                                onClick={() => handleRemovePricing(product)}
                                disabled={removingProductId === product.productId}
                                className="text-red-600 hover:underline text-sm disabled:opacity-50"
                              >
                                {removingProductId === product.productId ? 'Removing...' : 'Remove'}
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })()}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <div className="bg-surface rounded-lg shadow p-6">
            <h3 className="font-bold text-text mb-4">Outlet Photo</h3>
            <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden mb-4 flex items-center justify-center">
              {outlet.imageUrl ? (
                <img
                  src={outlet.imageUrl}
                  alt={outlet.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="text-4xl text-gray-400">📸</span>
              )}
            </div>
            <label className="block">
              <span className="sr-only">Upload photo</span>
              <input
                type="file"
                accept="image/*"
                onChange={handlePhotoUpload}
                disabled={isUploadingPhoto}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:bg-primary file:text-white file:cursor-pointer hover:file:bg-primary-600"
              />
            </label>
            {isUploadingPhoto && (
              <p className="text-sm text-text-muted mt-2">Uploading...</p>
            )}
          </div>

          {/* PIN Management */}
          <div className="bg-surface rounded-lg shadow p-6">
            <h3 className="font-bold text-text mb-4">🔐 Authentication PINs</h3>
            <p className="text-sm text-text-muted mb-6">
              Set separate 4-6 digit PINs for Customer and Capture roles
            </p>
            
            {/* Customer PIN */}
            <form onSubmit={handleUpdateCustomerPin} className="space-y-4 mb-6">
              <div>
                <label htmlFor="customer-pin" className="block text-sm font-medium text-text mb-2">
                  Customer PIN
                </label>
                <input
                  id="customer-pin"
                  type="password"
                  inputMode="numeric"
                  pattern="\d{4,6}"
                  value={customerPinValue}
                  onChange={(e) => setCustomerPinValue(e.target.value)}
                  placeholder="Enter 4-6 digits"
                  maxLength={6}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  disabled={isUpdatingCustomerPin}
                />
                <p className="text-xs text-text-muted mt-1">
                  Used for customer-facing features in the booth app
                </p>
              </div>
              <button
                type="submit"
                disabled={isUpdatingCustomerPin || !customerPinValue}
                className="w-full bg-primary text-white py-2 px-4 rounded-md hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isUpdatingCustomerPin ? 'Updating...' : 'Update Customer PIN'}
              </button>
              {customerPinSuccess && (
                <div className="text-sm text-green-600 text-center">
                  ✓ Customer PIN updated successfully
                </div>
              )}
            </form>

            {/* Divider */}
            <div className="border-t border-gray-200 my-6"></div>

            {/* Capture PIN */}
            <form onSubmit={handleUpdateCapturePin} className="space-y-4">
              <div>
                <label htmlFor="capture-pin" className="block text-sm font-medium text-text mb-2">
                  Capture PIN
                </label>
                <input
                  id="capture-pin"
                  type="password"
                  inputMode="numeric"
                  pattern="\d{4,6}"
                  value={capturePinValue}
                  onChange={(e) => setCapturePinValue(e.target.value)}
                  placeholder="Enter 4-6 digits"
                  maxLength={6}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  disabled={isUpdatingCapturePin}
                />
                <p className="text-xs text-text-muted mt-1">
                  Used for photo capture features in the booth app
                </p>
              </div>
              <button
                type="submit"
                disabled={isUpdatingCapturePin || !capturePinValue}
                className="w-full bg-primary text-white py-2 px-4 rounded-md hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isUpdatingCapturePin ? 'Updating...' : 'Update Capture PIN'}
              </button>
              {capturePinSuccess && (
                <div className="text-sm text-green-600 text-center">
                  ✓ Capture PIN updated successfully
                </div>
              )}
            </form>
          </div>

          {status && (
            <div className="bg-surface rounded-lg shadow p-6">
              <h3 className="font-bold text-text mb-4">Activity</h3>
              <div className="space-y-3">
                <div>
                  <div className="text-sm text-text-muted">Active Transactions</div>
                  <div className="text-2xl font-bold text-text">{status.activeTransactions}</div>
                </div>
                <div>
                  <div className="text-sm text-text-muted">Queued Print Jobs</div>
                  <div className="text-2xl font-bold text-text">{status.queuedPrintJobs}</div>
                </div>
              </div>
            </div>
          )}

          <div className="bg-surface rounded-lg shadow p-6">
            <h3 className="font-bold text-text mb-4">Actions</h3>
            <div className="space-y-2">
              <button
                onClick={handleDelete}
                className="w-full bg-red-600 text-white py-2 px-4 rounded-md hover:bg-red-700"
              >
                Delete Outlet
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
